/**
 * Shared production-brief read model (Task 210) — surfaces the EXISTING
 * Task 108A/108B production-brief contract for read-only consumers (the
 * Designer Workspace today; future video/production surfaces later).
 *
 * REUSE, not a new schema: this module reuses the existing contract TYPES from
 * `../../api/types` — `ContentVariantProductionBrief`, `ContentVariantRow`,
 * `ContentVariantPlatform`, `ContentVariantPlatformSpecific`. It does NOT
 * define a parallel brief schema.
 *
 * The Arabic label map + field-key lists + read/format rules mirror the
 * equivalents inside `ContentPage.tsx` (Task 108A/108B). They are intentionally
 * NOT extracted out of `ContentPage.tsx`: the Task 108B contract test
 * (`ContentPage.task108B.test.ts`) is a source-grep that pins those `const`
 * definitions INSIDE `ContentPage.tsx` (e.g. `const PRODUCTION_BRIEF_STATIC_FIELDS:[...]`),
 * so moving them would break that merged contract test. De-duplicating
 * ContentPage onto this module (and repointing the 108B pins) is a clean
 * follow-up. Both encode the SAME 108A/108B keys, labels, and formatting.
 */

import type {
  ContentVariantPlatform,
  ContentVariantPlatformSpecific,
  ContentVariantProductionBrief,
  ContentVariantRow,
} from '../../api/types';

// Re-export the contract types so consumers import the brief vocabulary from
// one place (still the single source in api/types — just re-exported).
export type {
  ContentVariantPlatform,
  ContentVariantPlatformSpecific,
  ContentVariantProductionBrief,
  ContentVariantRow,
};

/** Arabic labels — mirror of `BRIEF_LABELS_AR` in ContentPage.tsx (108A) + the
 *  variant-level designer extras (caption / hashtags / link). */
export const BRIEF_LABELS_AR = {
  onImageText: 'نص التصميم',
  visualDirection: 'التوجه البصري',
  creativeBrief: 'موجز التصميم',
  cta: 'الدعوة للإجراء',
  assetFormat: 'نوع الأصل',
  caption: 'التعليق',
  hashtags: 'الوسوم',
  link: 'الرابط',
  hook: 'الخطاف',
  script: 'السكربت',
  shotList: 'قائمة اللقطات',
  onScreenText: 'النصوص على الشاشة',
  audioDirection: 'توجيه الصوت',
  durationSeconds: 'مدة الفيديو (ثوانٍ)',
} as const;

/** `asset_format` soft-enum → Arabic — mirror of ContentPage's
 *  `ASSET_FORMAT_OPTIONS_AR` (108A). */
export const ASSET_FORMAT_OPTIONS_AR: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: 'static_image', label: 'صورة ثابتة' },
  { value: 'story', label: 'ستوري' },
  { value: 'carousel', label: 'كاروسيل' },
];

/** Video platforms — mirror of `isVideoPlatform` in ContentPage.tsx. */
export function isVideoBriefPlatform(platform: ContentVariantPlatform): boolean {
  return platform === 'instagram_reel' || platform === 'tiktok_video';
}

export interface BriefField {
  readonly key: string;
  readonly label: string;
}

/**
 * Designer-relevant STATIC / image fields, read from `platform_specific`
 * (plus the variant-level `caption`, rendered separately). These are the
 * fields a designer needs for an image/static task — the goal's set:
 * on-image text, visual direction, creative brief, asset format, CTA,
 * hashtags, link.
 */
export const DESIGNER_STATIC_BRIEF_FIELDS: ReadonlyArray<BriefField> = [
  { key: 'on_image_text', label: BRIEF_LABELS_AR.onImageText },
  { key: 'visual_direction', label: BRIEF_LABELS_AR.visualDirection },
  { key: 'creative_brief', label: BRIEF_LABELS_AR.creativeBrief },
  { key: 'asset_format', label: BRIEF_LABELS_AR.assetFormat },
  { key: 'cta', label: BRIEF_LABELS_AR.cta },
  { key: 'hashtags', label: BRIEF_LABELS_AR.hashtags },
  { key: 'link', label: BRIEF_LABELS_AR.link },
];

/**
 * VIDEO-only brief fields. Per the goal these are NOT shown as primary
 * designer fields unless present (the designer workspace surfaces them only
 * when the brief actually carries them, ready for the future video workspace).
 */
export const VIDEO_BRIEF_FIELDS: ReadonlyArray<BriefField> = [
  { key: 'hook', label: BRIEF_LABELS_AR.hook },
  { key: 'script', label: BRIEF_LABELS_AR.script },
  { key: 'shot_list', label: BRIEF_LABELS_AR.shotList },
  { key: 'on_screen_text', label: BRIEF_LABELS_AR.onScreenText },
  { key: 'audio_direction', label: BRIEF_LABELS_AR.audioDirection },
  { key: 'duration_seconds', label: BRIEF_LABELS_AR.durationSeconds },
];

/**
 * Read + format a single brief field out of `platform_specific` for read-only
 * display. Mirror of ContentPage's `readProductionBriefField` (108B):
 *   - `asset_format` → mapped through `ASSET_FORMAT_OPTIONS_AR` (raw value
 *     falls through if the enum gains a value).
 *   - `duration_seconds` → `${n} ثانية` for positive integers; a positive
 *     NON-integer falls through to the raw `String(n)` (parity with
 *     ContentPage); null if not a positive finite number.
 *   - `hashtags` → if an array, space-joins the non-empty string entries
 *     (the only designer-extra beyond ContentPage's string-only read).
 *   - all other keys → trimmed string, or null when empty/missing.
 * Returns null when the field should be hidden (the renderer skips empty rows).
 */
export function readBriefField(
  platformSpecific: ContentVariantPlatformSpecific | null | undefined,
  key: string,
): string | null {
  if (!platformSpecific) return null;
  const raw = platformSpecific[key];
  if (raw === null || raw === undefined) return null;
  if (key === 'duration_seconds') {
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null;
    return Number.isInteger(raw) ? `${raw} ثانية` : String(raw);
  }
  if (key === 'hashtags' && Array.isArray(raw)) {
    const tags = raw
      .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
      .map((t) => t.trim());
    return tags.length > 0 ? tags.join(' ') : null;
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (key === 'asset_format') {
    const match = ASSET_FORMAT_OPTIONS_AR.find((opt) => opt.value === trimmed);
    return match ? match.label : trimmed;
  }
  return trimmed;
}

/** Static brief fields that are actually present (non-empty) for this variant. */
export function presentStaticBriefFields(
  ps: ContentVariantPlatformSpecific | null | undefined,
): ReadonlyArray<{ readonly label: string; readonly value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  for (const f of DESIGNER_STATIC_BRIEF_FIELDS) {
    const value = readBriefField(ps, f.key);
    if (value !== null) out.push({ label: f.label, value });
  }
  return out;
}

/**
 * Video brief fields that are actually present in `platform_specific`
 * (presence-only). The PLATFORM gate ("video fields only for video platforms")
 * is applied by the caller: `hasAnyDesignerBrief` and the Designer Workspace
 * renderer both gate this on `isVideoBriefPlatform(variant.platform)`, so a
 * static/image task never surfaces video fields — mirroring ContentPage.
 */
export function presentVideoBriefFields(
  ps: ContentVariantPlatformSpecific | null | undefined,
): ReadonlyArray<{ readonly label: string; readonly value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  for (const f of VIDEO_BRIEF_FIELDS) {
    const value = readBriefField(ps, f.key);
    if (value !== null) out.push({ label: f.label, value });
  }
  return out;
}

/**
 * Does this variant carry ANY designer-relevant brief content (incl. caption)?
 * Video-only fields count ONLY for video platforms (parity with ContentPage's
 * `variantHasAnyProductionBrief`, which selects its field set by platform): a
 * static/image task never counts video fields, even from legacy/dirty data.
 */
export function hasAnyDesignerBrief(variant: ContentVariantRow): boolean {
  if (typeof variant.caption === 'string' && variant.caption.trim().length > 0) return true;
  const ps = variant.platform_specific;
  if (presentStaticBriefFields(ps).length > 0) return true;
  return isVideoBriefPlatform(variant.platform) && presentVideoBriefFields(ps).length > 0;
}

/** The empty-state copy when a variant has no production brief yet. */
export const EMPTY_BRIEF_AR = 'لا يوجد موجز إنتاج لهذه المهمة بعد.';
